import * as Sentry from "@sentry/nextjs";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { BLOCKS } from "@contentful/rich-text-types";
const Modal = dynamic(() => import("@/components/molecules/modal"));

import { richContentOptions } from "@/components/content-options";
import Image from "@/components/image";
import { recordConversion, Conversion } from "@/utils/conversions";
import dynamic from "next/dynamic";
import Contact from "@/components/forms/contact";
import Link from "next/link";
import QuizPromo from "../quiz-promo";

// let getSiblings = function (e) {
//   // for collecting siblings
//   let siblings = [];
//   // if no parent, return no sibling
//   if (!e.parentNode) {
//     return siblings;
//   }
//   // first child of the parent node
//   let sibling = e.parentNode.firstChild;

//   // collecting siblings
//   while (sibling) {
//     if (sibling.nodeType === 1 && sibling !== e) {
//       siblings.push(sibling);
//     }
//     sibling = sibling.nextSibling;
//   }
//   return siblings;
// };

function I({ fields }) {
  const aspect =
    fields.image.fields.file.details.image.height /
    fields.image.fields.file.details.image.width;
  const aspectClass = aspect === 1 ? "aspect-1 p-1" : "aspect-w-12 aspect-h-7";
  const imgClass =
    aspect === 1
      ? "rounded-lg shadow-lg"
      : "rounded-lg shadow-lg object-cover object-center";
  return (
    <div className="relative text-base w-full mx-auto max-w-prose lg:max-w-none">
      <figure>
        <div className={`${aspectClass} rounded-lg shadow-lg`}>
          <Image alt="" fields={fields.image} className={imgClass} fill />
        </div>
        <figcaption className="mt-3 flex text-sm text-gray-500">
          <svg
            className="flex-none w-5 h-5 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
              clipRule="evenodd"
            />
          </svg>
          <span className="ml-2">{fields.image.fields.title}</span>
        </figcaption>
      </figure>
    </div>
  );
}

const Paragraph = (node, children) => {
  const isEmptyChildren = children?.toString().trim() === "";
  if (isEmptyChildren) return null;
  return <span className="mt-3 text-base text-gray-500">{children}</span>;
};

export default function Content({ fields, ...rest }) {
  const [state, setState] = useState({
    brochure: false,
    brochureDone: false,
  });

  const brochureForm = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
    },
  });

  const brochureSubmit = async (data) => {
    try {
      setState((s) => ({ ...s, error: null }));
      const req = await fetch("/api/callback/", {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          // type: "callback",
          courses: [
            {
              id: fields?.id,
              title: fields.title,
            },
          ],
        }),
      });

      const res = await req.json();

      if (res && res.success) {
        setState({
          brochure: true,
          brochureDone: true,
        });

        recordConversion({
          send_to: Conversion.callback,
        });

        (window as any).fbq && (window as any).fbq("track", "Contact");

        (window as any).heap &&
          (window as any).heap.addUserProperties({
            ...data,
          });

        (window as any).heap &&
          (window as any).heap.track("Brochure Request", {
            ...data,
          });

        // @ts-ignore
        klaviyo &&
          // @ts-ignore
          klaviyo.push([
            "identify",
            {
              $email: data.email,
              $first_name: data.first_name,
              $last_name: data.last_name,
              $phone_number: data.phone_number,
            },
          ]);

        // @ts-ignore
        klaviyo &&
          // @ts-ignore
          klaviyo.push([
            "track",
            "Brochure Request",
            {
              $email: data.email,
              $first_name: data.first_name,
              $last_name: data.last_name,
              $phone_number: data.phone_number,
              $path_name: window.location.pathname,
            },
          ]);
      } else {
        setState((s) => ({
          ...s,
          error: "Error processing request, Please try again",
        }));
      }
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
      setState((s) => ({
        ...s,
        error: "Error processing request, Please try again",
      }));
    }
  };

  const options = {
    ...richContentOptions,
    renderNode: {
      ...richContentOptions.renderNode,
      [BLOCKS.PARAGRAPH]: Paragraph,
    },
  };

  return (
    <>
      <div className="bg-white mb-16">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="hidden lg:block bg-gray-50 absolute top-0 bottom-0 left-3/4 w-screen"></div>
          <div className="mx-auto text-base max-w-prose lg:max-w-none">
            {fields.title && (
              <h3 className="mb-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl max-w-xl">
                {fields.title}
              </h3>
            )}
            {fields.highlight && (
              <h2 className="text-base text-brand-600 font-semibold tracking-wide uppercase max-w-lg">
                {fields.highlight}
              </h2>
            )}
          </div>
          <div
            className={
              fields.aside
                ? "mt-8 grid lg:grid-rows-1 gap-8 lg:grid-cols-2"
                : "mt-8 grid lg:grid-rows-1 gap-8 lg:grid-cols-2"
            }
          >
            <div className="mt-8 relative">
              <svg
                className="hidden lg:block absolute top-0 right-0 -mt-20 -mr-20"
                width="404"
                height="384"
                fill="none"
                viewBox="0 0 404 384"
                aria-hidden="true"
              >
                <defs>
                  <pattern
                    id="de316486-4a29-4312-bdfc-fbce2132a2c1"
                    x="0"
                    y="0"
                    width="20"
                    height="20"
                    patternUnits="userSpaceOnUse"
                  >
                    <rect
                      x="0"
                      y="0"
                      width="4"
                      height="4"
                      className="text-gray-200"
                      fill="currentColor"
                    />
                  </pattern>
                </defs>
                <rect
                  width="404"
                  height="384"
                  fill="url(#de316486-4a29-4312-bdfc-fbce2132a2c1)"
                />
              </svg>
              {fields.image && !fields.video && <I fields={fields} />}

              {fields.video && (
                <div className="relative text-base w-full mx-auto max-w-prose lg:max-w-none">
                  <figure>
                    <div className="aspect-w-12 aspect-h-7 rounded-lg shadow-lg">
                      <video
                        src={fields.video.fields.file.url}
                        autoPlay
                        playsInline
                        muted
                        loop
                        controls
                        controlsList="nodownload nofullscreen noremoteplayback"
                        onContextMenu={() => false}
                        className="rounded-lg shadow-lg object-cover object-center"
                      />
                    </div>
                    <figcaption className="mt-3 flex text-sm text-gray-500">
                      <svg
                        className="flex-none w-5 h-5 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="ml-2">{fields.video.fields.title}</span>
                    </figcaption>
                  </figure>
                </div>
              )}

              <div className="rounded-lg bg-brand-100 p-4 mt-6 md:mt-16 md:mb-8 shadow">
                {!state.brochure && !state.brochureDone && (
                  <>
                    <p className="flex items-center font-medium text-sm lg:text-base">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>Speak with a course advisor</span>
                    </p>
                    <p className="text-xs mt-4 lg:text-sm">
                      Interested in learning more about this course or other
                      courses we have available at the Cosmetic College?
                      Schedule a call with one of our course advisors to get all
                      the information you need to start training.
                    </p>
                    {/* <button
                      type="button"
                      className="text-sm bg-brand-600 py-2 px-5 text-center rounded text-white mt-2 float-right"
                      onClick={() =>
                        setState((s) => ({ ...s, brochure: true }))
                      }
                    >
                      Schedule Call
                    </button> */}
                    <Link
                      href="/request-a-callback"
                      className="text-sm bg-brand-600 py-2 px-5 text-center rounded text-white mt-2 float-right"
                    >
                      Schedule Call
                    </Link>
                  </>
                )}
                {state.brochure && !state.brochureDone && (
                  <form onSubmit={brochureForm.handleSubmit(brochureSubmit)}>
                    <p className="flex items-center font-medium text-sm lg:text-base">
                      <span>Speak with a course advisor</span>
                    </p>
                    <div className="md:flex md:space-x-2">
                      <div className="mt-4 flex-1">
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-gray-700"
                        >
                          First name
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            name="name"
                            id="name"
                            {...brochureForm.register("first_name", {
                              required: true,
                            })}
                            className="shadow-sm focus:ring-brand-500 focus:border-brand-600 block w-full sm:text-sm border-gray-300 rounded-md"
                            placeholder="Your name"
                          />
                        </div>
                        {brochureForm.formState.errors.first_name && (
                          <span className="text-red-600 text-sm">
                            Name is required
                          </span>
                        )}
                      </div>
                      <div className="mt-4 flex-1">
                        <label
                          htmlFor="surname"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Last name
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            name="surname"
                            id="surname"
                            {...brochureForm.register("last_name", {
                              required: true,
                            })}
                            className="shadow-sm focus:ring-brand-500 focus:border-brand-600 block w-full sm:text-sm border-gray-300 rounded-md"
                            placeholder="Last name"
                          />
                        </div>
                        {brochureForm.formState.errors.last_name && (
                          <span className="text-red-600 text-sm">
                            Last name is required
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="md:flex md:space-x-2">
                      <div className="mt-4 flex-1">
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Email
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            name="email"
                            id="email"
                            {...brochureForm.register("email", {
                              required: true,
                            })}
                            className="shadow-sm focus:ring-brand-500 focus:border-brand-600 block w-full sm:text-sm border-gray-300 rounded-md"
                            placeholder="you@example.com"
                          />
                        </div>
                        {brochureForm.formState.errors.email && (
                          <span className="text-red-600 text-sm">
                            Email is required
                          </span>
                        )}
                      </div>
                      <div className="mt-4 flex-1">
                        <label
                          htmlFor="phone"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Phone number
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            name="phone"
                            id="phone"
                            {...brochureForm.register("phone_number", {
                              required: true,
                            })}
                            className="shadow-sm focus:ring-brand-500 focus:border-brand-600 block w-full sm:text-sm border-gray-300 rounded-md"
                            placeholder="Phone number"
                          />
                        </div>
                        {brochureForm.formState.errors.phone_number && (
                          <span className="text-red-600 text-sm">
                            Phone number is required
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="text-sm bg-brand-600 py-2 px-5 text-center rounded text-white mt-4 float-right hover:bg-brand-700"
                      onClick={(e) => {
                        brochureForm.handleSubmit(brochureSubmit)();
                      }}
                    >
                      Book Call
                    </button>
                  </form>
                )}
                {state.brochureDone && (
                  <>
                    <p className="flex items-center font-medium text-sm lg:text-base">
                      <span>Details Received</span>
                    </p>
                    <p className="text-xs mt-4 lg:text-sm">
                      We have sucessfully received your details and this has
                      been passed on to the team. Your brochure is on its way to
                      the provided email address
                    </p>
                  </>
                )}
                <div className="clear-both" />
                <div className="mx-auto max-w-7xl w-full mt-6">
                  <QuizPromo variant="compact" />
                </div>
              </div>
            </div>
            {fields.aside && (
              <div className="relative row-start-3 lg:col-span-1 lg:col-start-2 lg:row-start-2">
                <div className="lg:mt-16">
                  <div className="text-gray-500 mx-auto lg:max-w-none">
                    {documentToReactComponents(fields.aside, options)}
                  </div>
                </div>
              </div>
            )}
            <div
              id="content-body"
              className="lg:row-span-2 lg:col-start-1 lg:row-start-1"
            >
              <div className="mt-5 prose prose-brand text-gray-500 mx-auto lg:max-w-none lg:row-start-1 lg:col-start-1">
                {documentToReactComponents(fields.content, richContentOptions)}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        open={state.brochureDone}
        close={() => {
          setState((s) => ({ ...s, brochureDone: false }));
          document.body.style.overflow = "auto";
        }}
        full={state.brochureDone}
        xlarge={!state.brochureDone}
      >
        <div className="h-full">
          <div className="bg-white">
            <div className="max-w-7xl mx-auto h-full">
              <iframe
                src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ28xd3NPsZQJIxeuaTCep2WJENG0LOYVTLTDZchVdRmkT3mhwe09k6LPa8iuTBuXaUEZY0iYOBQ?gv=true"
                style={{ border: 0 }}
                width="100%"
                height="700"
                loading="lazy"
                frameBorder="0"
              ></iframe>
            </div>
          </div>
        </div>
      </Modal>
      {rest.primary &&
      rest.primary.fields &&
      rest.primary.fields.url === "/franchise-with-us" ? (
        <div>
          <Contact franchise />
        </div>
      ) : null}
    </>
  );
}
